/*
 *  Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.0.1
 * @since 1.0.1
 */

import { cloneElement } from 'react';

import {
    DragDropContext,
    Draggable,
    DraggableLocation,
    DraggableProvided,
    Droppable,
    DroppableProvided,
    DropResult,
} from 'react-beautiful-dnd';

import { DraggableItemType, IDragAndDropContainerProps, IDraggableItem, IDragMoveResult } from './dndContainerTypes';
import { DragAndDropColumnContainerStyled, DragAndDropContainerStyled } from './styled/DragAndDropContainerStyled';

const reorder = <Item extends IDraggableItem>(list: Item[], startIndex: number, endIndex: number): Item[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const move = <Item extends IDraggableItem>(itemsSource: Item[],
                                           itemsDestination: Item[],
                                           droppableSource: DraggableLocation,
                                           droppableDestination: DraggableLocation): IDragMoveResult<Item> => {

    const sourceClone = Array.from(itemsSource);
    const destinationClone = Array.from(itemsDestination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destinationClone.splice(droppableDestination.index, 0, removed);

    const result: DraggableItemType<Item> = {};
    result[droppableSource.droppableId] = sourceClone;
    result[droppableDestination.droppableId] = destinationClone;

    return { result: result };
};

const DragAndDropContainer =
    <Item extends IDraggableItem, T>(props: IDragAndDropContainerProps<Item, T>) => {

        const getList = (id: string) => props.columns[id];

        const onDragEnd = (result: DropResult) => {
            const { destination, source } = result;
            // if dropped outside the list
            if (!destination || props.isDropDisable) {
                return;
            }
            const sourceId = source.droppableId;
            const destinationId = destination.droppableId;

            if (sourceId === destinationId) {
                const items = reorder(
                    getList(sourceId),
                    source.index,
                    destination.index,
                );

                props.saveReorderResult(items, sourceId);
            } else {
                const moveResult = move(
                    getList(sourceId),
                    getList(destinationId),
                    source,
                    destination,
                );

                props.saveMoveResult(moveResult);
            }
        };

        return (
            <DragDropContext onDragEnd={onDragEnd}>
                <DragAndDropContainerStyled>
                    {
                        Object.keys(props.columns).map((draggableId: string) => (
                            <Droppable
                                droppableId={draggableId}
                                isDropDisabled={props.isDropDisable}
                                key={draggableId}
                            >
                                {
                                    (provided: DroppableProvided) => (
                                        <DragAndDropColumnContainerStyled ref={provided.innerRef}>
                                            {
                                                props.columns[draggableId].map((item: Item, index: number) => (
                                                    <Draggable
                                                        draggableId={item.id}
                                                        index={index}
                                                        isDragDisabled={props.isDropDisable}
                                                        key={item.id}
                                                    >
                                                        {
                                                            (provided: DraggableProvided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    {
                                                                        cloneElement(
                                                                            item.render(),
                                                                            { translation: props.translation },
                                                                        )
                                                                    }
                                                                </div>
                                                            )
                                                        }
                                                    </Draggable>
                                                ))
                                            }
                                            {provided.placeholder}
                                        </DragAndDropColumnContainerStyled>
                                    )
                                }
                            </Droppable>
                        ))
                    }
                </DragAndDropContainerStyled>
            </DragDropContext>
        );
    };

export default DragAndDropContainer;
